import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { SettingsView, UpdateSettings } from '@/api/types';

/**
 * Runtime configuration, so the practice's Ontraport credentials and super field ids can be
 * entered here instead of shipped in a config file. Saves land in the overrides file the
 * host layers over appsettings, and the next run picks them up — no redeploy.
 *
 * Secrets are write-only: the server sends back "set / last four" and never the value, so
 * a blank box means "leave the installed one alone".
 */
interface Props {
  /** Held by the parent so switching console tabs mid-edit doesn't discard what's typed. */
  draft: UpdateSettings | null;
  onDraftChange: (draft: UpdateSettings) => void;
}

export function SettingsPanel({ draft, onDraftChange }: Props) {
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ['settings'],
    queryFn: api.getSettings,
    // No refetch on window focus: this screen is a form. Coming back to the tab used to
    // re-seed the inputs from the server and wipe whatever was half-typed.
    refetchOnWindowFocus: false,
  });

  const save = useMutation({
    mutationFn: (update: UpdateSettings) => api.updateSettings(update),
    onSuccess: (fresh) => {
      queryClient.setQueryData(['settings'], fresh);
      // Clear the secret boxes on success: they're write-only, and leaving the typed value
      // on screen suggests it's stored here rather than server-side.
      onDraftChange({ ...(draft ?? {}), ontraport: { ...draft?.ontraport, key: '' }, webhook: { secret: '' } });
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2500);
    },
  });

  const [saved, setSaved] = useState(false);

  const form = draft ?? {};
  const setForm = onDraftChange;

  // Seed the inputs from the server exactly once — `draft` being non-null means the user
  // already has a version of this form, and re-seeding would overwrite their edits.
  useEffect(() => {
    if (!data || draft) return;
    onDraftChange({
      ontraport: { appId: data.ontraport.appId ?? '', key: '', baseUrl: data.ontraport.baseUrl ?? '' },
      fields: {
        tfn: data.fields.tfn ?? '',
        totalSuperBalance: data.fields.totalSuperBalance ?? '',
        fundCount: data.fields.fundCount ?? '',
        fundNames: data.fields.fundNames ?? '',
        primaryFundName: data.fields.primaryFundName ?? '',
        primaryFundUsi: data.fields.primaryFundUsi ?? '',
        primaryFundAccountNumber: data.fields.primaryFundAccountNumber ?? '',
        primaryFundBalance: data.fields.primaryFundBalance ?? '',
        lastSearchedAt: data.fields.lastSearchedAt ?? '',
        searchStatus: data.fields.searchStatus ?? '',
        lodgementConfirmedAt: data.fields.lodgementConfirmedAt ?? '',
      },
      ato: { url: data.ato.url ?? '' },
      webhook: { secret: '', defaultTaxYear: data.webhook.defaultTaxYear ?? null },
      runner: data.runner,
    });
  }, [data, draft, onDraftChange]);

  if (!data || !form.fields || !form.runner) {
    return <div className="panel p-6 text-sm text-zinc-500">Loading settings…</div>;
  }

  const webhookUrl = `${window.location.origin}${data.webhook.endpointPath}?secret=…`;

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <section className="panel p-5">
        <h2 className="mb-4 text-sm font-medium text-zinc-100">Ontraport</h2>
        <div className="space-y-3">
          <Text
            label="App ID"
            value={form.ontraport?.appId ?? ''}
            onChange={(appId) => setForm({ ...form, ontraport: { ...form.ontraport, appId } })}
          />
          <Text
            label={data.ontraport.keySet ? `API key (installed ••••${data.ontraport.keyLast4})` : 'API key'}
            placeholder={data.ontraport.keySet ? 'leave blank to keep' : 'paste key'}
            value={form.ontraport?.key ?? ''}
            onChange={(key) => setForm({ ...form, ontraport: { ...form.ontraport, key } })}
          />
          <p className="text-xs text-zinc-500">
            {data.ontraport.configured ? 'Credentials installed.' : 'Not configured — lookups will fail.'}
          </p>
        </div>
      </section>

      <section className="panel p-5">
        <h2 className="mb-1 text-sm font-medium text-zinc-100">ATO &amp; webhook</h2>
        <p className="mb-4 text-xs text-zinc-500">
          Paste the webhook URL into the Ontraport signature automation:
          <code className="mt-1 block font-mono text-[11px] break-all text-zinc-400">{webhookUrl}</code>
        </p>
        <div className="space-y-3">
          <Text
            label="Ato.Api URL"
            placeholder="https://ato.example.com"
            value={form.ato?.url ?? ''}
            onChange={(url) => setForm({ ...form, ato: { url } })}
          />
          <Text
            label={data.webhook.secretSet ? `Webhook secret (set ••••${data.webhook.secretLast4})` : 'Webhook secret'}
            placeholder={data.webhook.secretSet ? 'leave blank to keep' : 'choose a long random string'}
            value={form.webhook?.secret ?? ''}
            onChange={(secret) => setForm({ ...form, webhook: { ...form.webhook, secret } })}
          />
          <Text
            label="Tax year this form collects"
            placeholder="e.g. 2026 — blank infers from the date"
            value={form.webhook?.defaultTaxYear?.toString() ?? ''}
            onChange={(value) =>
              setForm({
                ...form,
                webhook: { ...form.webhook, defaultTaxYear: value.trim() ? parseInt(value, 10) : null },
              })
            }
          />
          <p className="text-xs text-zinc-500">
            The signature form is per-year, so naming its year beats guessing from the calendar — a
            late signature arriving after 30 June would otherwise be filed under the new year. A year
            sent in the submission still wins.
          </p>
        </div>
      </section>

      <section className="panel p-5 lg:col-span-2">
        <h2 className="mb-1 text-sm font-medium text-zinc-100">Super field mapping</h2>
        <p className="mb-4 text-xs text-zinc-500">
          Ontraport field ids (e.g. <code className="font-mono">f1234</code>). Blank means "don't write this
          field" — everything is still recorded here either way.
        </p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {(
            [
              ['tfn', 'TFN field (read)'],
              ['totalSuperBalance', 'Total super balance'],
              ['fundCount', 'Number of funds'],
              ['fundNames', 'Fund names'],
              ['primaryFundName', 'Largest fund — name'],
              ['primaryFundUsi', 'Largest fund — USI'],
              ['primaryFundAccountNumber', 'Largest fund — account'],
              ['primaryFundBalance', 'Largest fund — balance'],
              ['lastSearchedAt', 'Last searched (date)'],
              ['searchStatus', 'Search status'],
              ['lodgementConfirmedAt', 'Lodgement confirmed (date)'],
            ] as const
          ).map(([key, label]) => (
            <Text
              key={key}
              label={label}
              value={form.fields?.[key] ?? ''}
              onChange={(value) =>
                setForm({ ...form, fields: { ...form.fields!, [key]: value } })
              }
            />
          ))}
        </div>
      </section>

      <section className="panel p-5 lg:col-span-2">
        <h2 className="mb-4 text-sm font-medium text-zinc-100">Runner</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Toggle
            label="Runner enabled"
            checked={form.runner.enabled}
            onChange={(enabled) => setForm({ ...form, runner: { ...form.runner!, enabled } })}
          />
          <Toggle
            label="Wait for lodgement"
            hint="Hold each search until the ATO confirms the return was lodged"
            checked={form.runner.requireLodgement}
            onChange={(requireLodgement) => setForm({ ...form, runner: { ...form.runner!, requireLodgement } })}
          />
          <Toggle
            label="Auto-request myGovID"
            hint="Ask for a login prompt when work is queued and the session is cold"
            checked={form.runner.autoRequestReauthentication}
            onChange={(autoRequestReauthentication) =>
              setForm({ ...form, runner: { ...form.runner!, autoRequestReauthentication } })
            }
          />
          <Number
            label="Poll interval (s)"
            value={form.runner.pollIntervalSeconds}
            onChange={(pollIntervalSeconds) => setForm({ ...form, runner: { ...form.runner!, pollIntervalSeconds } })}
          />
          <Number
            label="Batch size"
            value={form.runner.batchSize}
            onChange={(batchSize) => setForm({ ...form, runner: { ...form.runner!, batchSize } })}
          />
          <Number
            label="Max attempts"
            value={form.runner.maxAttempts}
            onChange={(maxAttempts) => setForm({ ...form, runner: { ...form.runner!, maxAttempts } })}
          />
          <Number
            label="ATO retry (min)"
            value={form.runner.atoUnavailableRetryMinutes}
            onChange={(atoUnavailableRetryMinutes) =>
              setForm({ ...form, runner: { ...form.runner!, atoUnavailableRetryMinutes } })
            }
          />
        </div>
      </section>

      <div className="flex items-center gap-3 lg:col-span-2">
        <button className="btn btn-primary" onClick={() => save.mutate(form)} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save settings'}
        </button>
        {saved && <span className="text-xs text-emerald-300">Saved — the next run uses these.</span>}
        {save.isError && <span className="text-xs text-rose-300">{(save.error as Error).message}</span>}
      </div>
    </div>
  );
}

function Text({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="block">
      <span className="field-label">{label}</span>
      <input
        className="field-input"
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function Number({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <label className="block">
      <span className="field-label">{label}</span>
      <input
        type="number"
        className="field-input"
        value={value}
        onChange={(event) => onChange(parseInt(event.target.value || '0', 10))}
      />
    </label>
  );
}

function Toggle({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2.5">
      <input
        type="checkbox"
        className="mt-0.5 size-4 rounded border-zinc-700 bg-zinc-950 accent-emerald-500"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span>
        <span className="block text-sm text-zinc-200">{label}</span>
        {hint && <span className="block text-xs text-zinc-500">{hint}</span>}
      </span>
    </label>
  );
}

export type { SettingsView };
